'use client';

import { Button } from '@components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import { Plus } from 'lucide-react';

import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import { useIsMobile } from '@hooks/use-mobile';
import {
  ActionSheet,
  ActionSheetGroup
} from '@components/mobile/overlays/action-sheet';
import type { OrganizationUserRole } from '@ent/permissions-shared';
import {
  CAPABILITIES,
  hasCapability,
  type Capability
} from '@utils/capabilities';
import { useCurrentOrgUser } from '../zero/use-current-user';
import {
  AddCreateMenuItemId,
  addCreateMenuLeaves,
  filterAddCreateMenu,
  getAddCreateMenu,
  getContextualAddCreateItemIds
} from '../utils/user-utils';
import { useOptionalEntitlements } from './billing/EntitlementsProvider';
import { FEATURES, type FeatureId } from '@billing/plans';
import { getHref, ROUTE } from '../utils/route-utils';
import { usePageChrome } from '@components/layout/page-chrome-context';

const CREATE_CAPABILITIES: Partial<Record<AddCreateMenuItemId, Capability>> = {
  [AddCreateMenuItemId.Add86Item]: CAPABILITIES.ManageUnavailableItems,
  [AddCreateMenuItemId.CreateBeverageMenu]: CAPABILITIES.ManageMenus,
  [AddCreateMenuItemId.CreateFoodMenu]: CAPABILITIES.ManageMenus,
  [AddCreateMenuItemId.CreateRecipe]: CAPABILITIES.ManageRecipes,
  [AddCreateMenuItemId.AddToManagerLog]: CAPABILITIES.CreateLogbookEntry,
  [AddCreateMenuItemId.InviteEmployee]: CAPABILITIES.ManageTeam
};

/** Plan feature each create action needs; locked items are hidden. */
const CREATE_FEATURES: Partial<Record<AddCreateMenuItemId, FeatureId>> = {
  [AddCreateMenuItemId.Add86Item]: FEATURES.EightySix,
  [AddCreateMenuItemId.CreateBeverageMenu]: FEATURES.Menus,
  [AddCreateMenuItemId.CreateFoodMenu]: FEATURES.Menus,
  [AddCreateMenuItemId.CreateRecipe]: FEATURES.HouseRecipes,
  [AddCreateMenuItemId.AddToManagerLog]: FEATURES.Logbook,
  [AddCreateMenuItemId.InviteEmployee]: FEATURES.Team
};

export type Props = {
  organization_id: string | undefined;
  /**
   * `sidebar` renders the full-width button (desktop dropdown / mobile action
   * sheet). `fab` renders the floating create button above the tab bar.
   * `island` is the Plus squircle next to the mobile tab island.
   * `chrome` is the acid-lime circle in the mobile header.
   */
  variant?: 'sidebar' | 'fab' | 'island' | 'chrome';
};

const AppAddCreateButton = ({
  organization_id,
  variant = 'sidebar'
}: Props) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const { chrome } = usePageChrome();
  const [showActionSheet, setShowActionSheet] = useState(false);
  const orgUser = useCurrentOrgUser(organization_id ?? '');
  const role = (orgUser?.role as OrganizationUserRole | undefined) ?? null;
  const scope = organization_id
    ? getContextualAddCreateItemIds(pathname, organization_id)
    : 'none';
  const entitlements = useOptionalEntitlements();
  const menuItems = filterAddCreateMenu(
    getAddCreateMenu(t, {
      onAdd86Item: () =>
        router.push(getHref(organization_id, [ROUTE.EIGHTY_SIX, 'add'])),
      onCreateBeverageMenu: () =>
        router.push(
          getHref(organization_id, [
            ROUTE.BEVERAGES,
            ROUTE.COCKTAIL_MENUS,
            'create-menu'
          ])
        ),
      onCreateFoodMenu: () =>
        router.push(
          getHref(organization_id, [
            ROUTE.FOOD,
            ROUTE.FOOD_MENUS,
            'create-menu'
          ])
        ),
      onCreateRecipe: () =>
        router.push(
          getHref(organization_id, [ROUTE.BEVERAGES, ROUTE.RECIPES, 'create'])
        ),
      onAddToManagerLog: () =>
        router.push(
          getHref(
            organization_id,
            [ROUTE.LOGBOOK],
            new URLSearchParams({ compose: '1' })
          )
        ),
      onInviteEmployee: () =>
        router.push(getHref(organization_id, ['add-employee']))
    }).map(item => ({
      ...item,
      children: item.children?.filter(child => {
        const requiredCapability =
          CREATE_CAPABILITIES[child.id as AddCreateMenuItemId];
        if (
          !requiredCapability ||
          !hasCapability(role ?? undefined, requiredCapability)
        ) {
          return false;
        }
        const requiredFeature =
          CREATE_FEATURES[child.id as AddCreateMenuItemId];
        return requiredFeature && entitlements
          ? entitlements.has(requiredFeature)
          : true;
      })
    })),
    scope
  );
  const leafActions = addCreateMenuLeaves(menuItems);
  const hidePlus = Boolean(chrome?.hideMobileTabs) || leafActions.length === 0;

  const selectItem = (onClick?: () => void) => {
    onClick?.();
  };

  const onPlusClick = () => {
    if (leafActions.length === 1) {
      selectItem(leafActions[0]?.onClick);
      return;
    }
    setShowActionSheet(true);
  };

  const plusLabel =
    leafActions.length === 1 && leafActions[0]?.label
      ? leafActions[0].label
      : t('common.add-create');

  const actionSheetGroups: ActionSheetGroup[] = menuItems
    .filter(item => item.id !== 'separator')
    .map(item => ({
      id: item.id,
      actions: (item.children ?? []).map(child => ({
        id: child.id,
        label: child.label,
        onSelect: () => selectItem(child.onClick)
      }))
    }));

  const actionSheet = (
    <ActionSheet
      open={showActionSheet}
      onOpenChange={setShowActionSheet}
      title={t('common.add-create')}
      groups={actionSheetGroups}
    />
  );

  if (hidePlus) {
    return null;
  }

  return (
    <>
      {variant === 'fab' ? (
        <>
          <button
            type="button"
            aria-label={plusLabel}
            onClick={onPlusClick}
            className="fixed right-4 bottom-[calc(var(--mobile-tabbar-height)+16px)] z-20 flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground [-webkit-text-fill-color:var(--primary-foreground)] shadow-cta transition-transform active:scale-95 desktop-chrome:hidden"
          >
            <Plus className="size-6" />
          </button>
          {actionSheet}
        </>
      ) : variant === 'island' ? (
        <>
          <button
            type="button"
            aria-label={plusLabel}
            onClick={onPlusClick}
            className="chrome-glass inline-flex size-[var(--mobile-tab-island-height)] shrink-0 items-center justify-center rounded-[var(--mobile-chrome-squircle)] text-foreground transition-transform duration-fast ease-out active:scale-[0.97]"
          >
            <Plus className="size-5" />
          </button>
          {actionSheet}
        </>
      ) : variant === 'chrome' ? (
        <>
          <Button
            type="button"
            size="icon"
            variant="primary"
            aria-label={plusLabel}
            onClick={onPlusClick}
            className="size-11 rounded-full"
          >
            <Plus className="size-5" aria-hidden />
          </Button>
          {actionSheet}
        </>
      ) : isMobile ? (
        <>
          <Button
            className="flex items-center w-full my-2"
            onClick={() => setShowActionSheet(true)}
          >
            <Plus />
            {t('common.add-create')}
          </Button>
          {actionSheet}
        </>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="flex items-center w-full my-2">
              <Plus />
              {t('common.add-create')}
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="min-w-56">
            {menuItems.map((item, i) => {
              if (item.id === 'separator') {
                return <DropdownMenuSeparator key={`${item.id}-${i}`} />;
              }

              return (
                <DropdownMenuGroup key={item.id}>
                  {item.children?.map((child, j) => (
                    <DropdownMenuItem
                      key={`${child.id}-${j}`}
                      onClick={() => selectItem(child.onClick)}
                    >
                      {child.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
};

export default AppAddCreateButton;
